
"use client";

import React, { createContext, useState, useContext, useEffect, useMemo, ReactNode } from 'react';
import axios from 'axios';
import { CartContextType, CartState } from '@/app/types/types';
import { useAuth } from './AuthContext';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartState | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const { user, openAuthModal } = useAuth();

  const fetchCart = async () => {
    if (!user) {
        setCart({ items: [] });
        setIsLoading(false);
        return;
    }

    setIsLoading(true);
    try {
      const { data } = await axios.get('/api/cart');
      if (data.success) {
        setCart(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch cart');
      }
    } catch (err) {
      console.error("Failed to fetch cart:", err);
      setError("Could not load your cart.");
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = async (productId: string, quantity: number, selectedOptions: string[]): Promise<boolean> => {
    if (!user) {
      openAuthModal();
      return false; // Action halted, return failure
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await axios.post('/api/cart', { productId, quantity, selectedOptions });
      if (data.success) {
        setCart(data.data);
        return true; // Action succeeded
      } else {
        throw new Error(data.error || 'Failed to add to cart');
      }
    } catch (err) {
      console.error("Failed to add to cart:", err);
      setError("Could not add item to cart.");
      return false; // Action failed
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (cartItemId: string, quantity: number): Promise<boolean> => {
    if (!user) {
      openAuthModal();
      return false; // Action halted
    }
    if (quantity < 1) {
      return await removeFromCart(cartItemId);
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await axios.put('/api/cart', { cartItemId, quantity });
      if (data.success) {
        setCart(data.data);
        return true; // Action succeeded
      } else {
        throw new Error(data.error || 'Failed to update quantity');
      }
    } catch (err) {
      console.error("Failed to update quantity:", err);
      setError("Could not update item quantity.");
      return false; // Action failed
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (cartItemId: string): Promise<boolean> => {
    if (!user) {
      openAuthModal();
      return false; // Action halted
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await axios.delete(`/api/cart?cartItemId=${cartItemId}`);
      if (data.success) {
        setCart(data.data);
        return true; // Action succeeded
      } else {
        throw new Error(data.error || 'Failed to remove from cart');
      }
    } catch (err) {
      console.error("Failed to remove from cart:", err);
      setError("Could not remove item from cart.");
      return false; // Action failed
    } finally {
      setIsLoading(false);
    }
  };

  const updateItem = async (cartItemId: string, quantity: number, selectedOptions: string[]): Promise<boolean> => {
    if (!user) {
      openAuthModal();
      return false;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await axios.put(`/api/cart/${cartItemId}`, { quantity, selectedOptions });
      if (data.success) {
        setCart(data.data);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Failed to update cart item:", err);
      setError("Could not update this item.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [user]);

  const itemCount = useMemo(() => {
    return cart?.items.reduce((total, item) => total + item.quantity, 0) || 0;
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    isLoading,
    error,
    fetchCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    updateItem,
    itemCount,
  }), [cart, isLoading, error, itemCount]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};